/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * @file AssignmentQuestionCard.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MathTextComponent from '../common/MathTextComponent';
import { AssignmentQuestionCardStylesRoot, classes } from './styles/QuestionTopicsModalStyles';

const getOptionLetter = (index) => String.fromCharCode(65 + index);

const AssignmentQuestionCard = ({
  questionLabel,
  questionStem,
  options,
  defaultExpanded
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  return (
    <AssignmentQuestionCardStylesRoot className={classes.questionCard}>
      <button
        type="button"
        className={classes.questionCardHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <Typography component="p" className={classes.questionCardTitle}>
          {questionLabel}
        </Typography>
        {isExpanded ? (
          <ExpandLessIcon className={classes.expandIcon} />
        ) : (
          <ExpandMoreIcon className={classes.expandIcon} />
        )}
      </button>
      {isExpanded && <hr className={classes.chapterDivider} />}
      {isExpanded && (
        <div className={classes.questionCardBody}>
          <MathTextComponent
            text={questionStem}
            className={classes.questionStem}
            component="p"
          />

          {options.length > 0 && (
            <div className={classes.optionsList}>
              {options.map((option, index) => {
                const isCorrect = option.correct;

                return (
                  <div
                    key={option.id}
                    className={`${classes.optionRow} ${isCorrect ? classes.optionRowCorrect : ''}`}
                  >
                    <span className={`${classes.optionPrefix} ${isCorrect ? classes.optionPrefixCorrect : ''}`}>
                      {getOptionLetter(index)}
                    </span>
                    <Box className={classes.optionContent}>
                      <MathTextComponent
                        text={option.label || ''}
                        className={`${classes.optionLabel} ${isCorrect ? classes.optionLabelCorrect : ''}`}
                      />
                    </Box>
                    {isCorrect && <DoneIcon style={{ color: '#006632', fontSize: 18 }} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </AssignmentQuestionCardStylesRoot>
  );
};

AssignmentQuestionCard.propTypes = {
  questionLabel: PropTypes.string.isRequired,
  questionStem: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    correct: PropTypes.bool
  })),
  defaultExpanded: PropTypes.bool
};

AssignmentQuestionCard.defaultProps = {
  options: [],
  defaultExpanded: false
};

export default AssignmentQuestionCard;
